import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  CircularProgress,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate, useParams } from "react-router-dom";
import useAxios from "../../../hooks/useAxios";
import './UpdateSubquestionPage.css';
import AddIcon from '@mui/icons-material/Add';
import ToggleSwitch from "../../../components/ToggleSwitch/ToggleSwitch";
import CustomInputField from "../../../components/CustomInputFields/CustomInputField/CustomInputField";
import Header from "../../../components/Header/Header";
const navLinks = [];

function UpdateSubquestionPage() {
  const { questionId, subquestionId } = useParams();
  const [subquestion, setSubquestion] = useState(null);
  const navigate = useNavigate();
  const axiosInstance = useAxios();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [body, setBody] = useState("");
  const [image, setImage] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [choices, setChoices] = useState([]);
  const [isMathBody, setIsMathBody] = useState(false);

  useEffect(() => {
    const fetchSubquestion = async () => {
      try {
        const response = await axiosInstance.get(`/quizzes/questions/${questionId}/subquestions/${subquestionId}`);
        if (response.data.success) {
          const fetchedSubquestion = response.data.subquestion;

          fetchedSubquestion.choices = fetchedSubquestion.choices
            ? JSON.parse(fetchedSubquestion.choices).map((choice) => ({
              ...choice,
              isMathMode: false, // Default to regular mode
            }))
            : [];

          setSubquestion(fetchedSubquestion);
          setBody(fetchedSubquestion.body);
          setImage(fetchedSubquestion.image);
          setOriginalImage(fetchedSubquestion.image);
          setChoices(fetchedSubquestion.choices);
        } else {
          setError(response.data.message || "Failed to fetch subquestion.");
        }
      } catch (err) {
        setError(err.response?.data?.message || "An unexpected error occurred.");
      } finally {
        setLoading(false);
      }
    };

    fetchSubquestion();
  }, [questionId, subquestionId]);

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setImageFile(file);
      setImage(URL.createObjectURL(file));
    }
  };

  const handleDeleteImage = () => {
    if (imageFile && image?.startsWith("blob:")) {
      URL.revokeObjectURL(image);
    }
    setImage(null);
    setImageFile(null);
  };

  const handleChoiceChange = (index, value) => {
    const updatedChoices = [...choices];
    updatedChoices[index].value = value;
    setChoices(updatedChoices);
  };

  const handleAddChoice = () => {
    const nextChoiceNumber = String.fromCharCode(65 + choices.length);
    setChoices([...choices, { number: nextChoiceNumber, value: "" }]);
  };

  const handleDeleteChoice = (index) => {
    const updatedChoices = choices.filter((_, i) => i !== index);
    // Renumber the remaining choices
    const renumberedChoices = updatedChoices.map((choice, i) => ({
      ...choice,
      number: String.fromCharCode(65 + i),
    }));
    setChoices(renumberedChoices);
  };

  const handleSave = async () => {
    const formData = new FormData();
    formData.append("body", body || "");
    if (imageFile) {
      formData.append("image", imageFile);
    } else if (image === null && originalImage !== null) {
      formData.append("image", "");
    }
    if (choices.length > 0) {
      choices.forEach((choice, index) => {
        formData.append(`choices[${index}][number]`, choice.number);
        formData.append(`choices[${index}][value]`, choice.value);
      });
    } else {
      formData.append("choices", []);
    }

    try {
      const response = await axiosInstance.post(
        `/quizzes/questions/${questionId}/subquestions/${subquestionId}`,
        formData
      );

      if (response.data.success) {
        // Redirect to quiz review
        navigate(`/quiz-review/${response.data.quiz_id}`);
      } else {
        setError(response.data.message || "Failed to update subquestion.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "An unexpected error occurred.");
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleBodyToggleChange = (event) => {
    setIsMathBody(event.target.checked);
  };

  const handleChoiceModeToggle = (index) => {
    const updatedChoices = [...choices];
    updatedChoices[index].isMathMode = !updatedChoices[index].isMathMode;
    setChoices(updatedChoices);
  };
  

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh">
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading Subquestion...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh" textAlign="center">
        <Typography variant="h6" color="error" gutterBottom>
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Header navLinks={navLinks} isScrollEffect={false} /> 
      <Box className="update-subquestion-container">
        <Typography variant="h4" className="page-title">
          Update Subquestion
        </Typography>

        <Card className="form-card">
          <CardContent>
          <ToggleSwitch
              mainLabel="subquestion body mode"
              labelLeft="Regular"
              labelRight="Math"
              checked={isMathBody}
              onChange={handleBodyToggleChange}
            />
            { isMathBody ?
            <CustomInputField
                body={body}
                onBodyChange={(value) => setBody(value)}
                label={"Subquestion Body"}
              />
            :
            <TextField
              label="Subquestion Body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              fullWidth
              multiline
              rows={3}
              className="custom-text-field"
              />
            }     

            <Box className="image-container">
              {image ? (
                <Box className="image-preview">
                  <img
                    src={
                      image.startsWith("blob:")
                        ? image
                        : `${process.env.REACT_APP_API_BASE_IMAGE_URL}${image}`
                    }
                    alt="Subquestion"
                    className="preview-image"
                  />
                  <Box className="image-actions">
                    <IconButton
                      color="primary"
                      onClick={() => document.getElementById("image-input").click()}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton color="error" onClick={handleDeleteImage}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Button
                  className="upload-button"
                  onClick={() => document.getElementById("image-input").click()}
                  startIcon={<AddPhotoAlternateIcon />}
                >
                  {imageFile ? "Change Image" : "Upload Image"}
                </Button>
              )}
              <input
                type="file"
                id="image-input"
                style={{ display: "none" }}
                accept="image/*"
                onChange={handleImageChange}
              />
            </Box>

            <Box className="choices-container">
              <Typography variant="h6" className="choices-title">
                Choices
              </Typography>
              {choices.map((choice, index) => (
                <>
                    <ToggleSwitch
                      mainLabel={`Mode`}
                      labelLeft="Regular"
                      labelRight="Math"
                      checked={choice.isMathMode}
                      onChange={() => handleChoiceModeToggle(index)}
                    />
                  <Box key={index} className="choice-row" display="flex" alignItems="center">
                    <Typography className="choice-number">{choice.number}.</Typography>
                    <Box flexGrow={1}>
                      {choice.isMathMode ? (
                        <CustomInputField
                          body={choice.value}
                          onBodyChange={(value) => {
                            const updatedChoices = [...choices];
                            updatedChoices[index].value = value;
                            setChoices(updatedChoices);
                          }}
                          label={`Choice ${choice.number}`}
                        />
                      ) : (
                        <TextField
                          value={choice.value}
                          onChange={(e) => {
                            const updatedChoices = [...choices];
                            updatedChoices[index].value = e.target.value;
                            setChoices(updatedChoices);
                          }}
                          fullWidth
                        />
                      )}
                    </Box>
                    
                    <IconButton
                      color="error"
                      onClick={() => {
                        const updatedChoices = choices.filter((_, i) => i !== index);
                        setChoices(
                          updatedChoices.map((choice, i) => ({
                            ...choice,
                            number: String.fromCharCode(65 + i), // Renumber choices dynamically
                          }))
                        );
                      }}
                      aria-label="delete-choice"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </>
              ))}
              <Button
                className="add-choice-button text-button"
                onClick={handleAddChoice}
              >
                <AddIcon className="add-icon"/>
                Add Choice
              </Button>
            </Box>

            <Grid container spacing={2} justifyContent="center" className="actions-container">
              <Grid item>
                <Button onClick={handleSave} className="primary-button">
                  Save
                </Button>
              </Grid>
              <Grid item>
                <Button onClick={handleCancel} className="secondary-button">
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </>
  );
}

export default UpdateSubquestionPage;
